import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { PREDICTIVE_INDEX_STUDENTS } from "@/api/queries/predictiveIndexStudents";
import {
  PredictiveIndexStudentsQueryVariables,
  PredictionIndexEnum,
  RisePredictiveIndexEnum,
  SortDirectionEnum,
  StudentListCommitIndexSortableFieldEnum,
  StudentListRisePredictiveIndexSortableFieldEnum,
  StudentListOrganizationSortableFieldEnum,
  FunnelStateEnum,
  StudentListArgumentInput,
  StudentListArgumentRiseInput,
  YesNoEnum,
} from "@/__generated__/types";

export interface PredictiveIndexStudent {
  id: string;
  name: string;
  score: number;
  previousIndex: PredictionIndexEnum | RisePredictiveIndexEnum | null;
  funnelState: FunnelStateEnum;
  indexChangedOn: string | null;
  imageUrl: string | null;
  inCommunity: boolean;
}

export type IndexType =
  | "commit"
  | "riseCommit"
  | "riseApply"
  | "riseMelt"
  | "riseYield"
  | "riseRetain";

export interface UsePredictiveIndexStudentsParams {
  orgId?: string;
  orgIdLoading?: boolean;
  orgIdError?: unknown;
  indexType: IndexType;
  currentIndex?: PredictionIndexEnum | RisePredictiveIndexEnum;
  previousIndex?: PredictionIndexEnum | RisePredictiveIndexEnum;
  funnelStates?: FunnelStateEnum[];
  inCommunity?: boolean;
  sortField?:
    | StudentListCommitIndexSortableFieldEnum
    | StudentListRisePredictiveIndexSortableFieldEnum
    | StudentListOrganizationSortableFieldEnum;
  sortDirection?: SortDirectionEnum;
  first?: number;
  last?: number;
  after?: string;
  before?: string;
}

export interface UsePredictiveIndexStudentsReturn {
  students: PredictiveIndexStudent[];
  loading: boolean;
  error: unknown;
  totalCount: number;
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startCursor: string | null;
    endCursor: string | null;
  };
  refetch: () => void;
}

interface StudentNode {
  id: string;
  profilePhoto?: {
    url?: string | null;
  } | null;
}

interface StudentOrganizationStatus {
  name?: string | null;
  commitScore?: number | null;
  previousCommitIndex?: PredictionIndexEnum | null;
  funnelState?: FunnelStateEnum | null;
  commitIndexChangedToHighOn?: string | null;
  commitIndexChangedToMediumOn?: string | null;
  commitIndexChangedToLowOn?: string | null;
  inCommunity?: YesNoEnum | null;
  rise?: {
    applyScore?: number | null;
    previousApplyIndex?: RisePredictiveIndexEnum | null;
    applyIndexChangedToHighOn?: string | null;
    applyIndexChangedToMediumHighOn?: string | null;
    applyIndexChangedToMediumOn?: string | null;
    applyIndexChangedToMediumLowOn?: string | null;
    applyIndexChangedToLowOn?: string | null;
    commitScore?: number | null;
    previousCommitIndex?: RisePredictiveIndexEnum | null;
    commitIndexChangedToHighOn?: string | null;
    commitIndexChangedToMediumHighOn?: string | null;
    commitIndexChangedToMediumOn?: string | null;
    commitIndexChangedToMediumLowOn?: string | null;
    commitIndexChangedToLowOn?: string | null;
    meltScore?: number | null;
    previousMeltIndex?: RisePredictiveIndexEnum | null;
    meltIndexChangedToHighOn?: string | null;
    meltIndexChangedToMediumHighOn?: string | null;
    meltIndexChangedToMediumOn?: string | null;
    meltIndexChangedToMediumLowOn?: string | null;
    meltIndexChangedToLowOn?: string | null;
    yieldScore?: number | null;
    previousYieldIndex?: RisePredictiveIndexEnum | null;
    yieldIndexChangedToHighOn?: string | null;
    yieldIndexChangedToMediumHighOn?: string | null;
    yieldIndexChangedToMediumOn?: string | null;
    yieldIndexChangedToMediumLowOn?: string | null;
    yieldIndexChangedToLowOn?: string | null;
    retainScore?: number | null;
    previousRetainIndex?: RisePredictiveIndexEnum | null;
    retainIndexChangedToHighOn?: string | null;
    retainIndexChangedToMediumHighOn?: string | null;
    retainIndexChangedToMediumOn?: string | null;
    retainIndexChangedToMediumLowOn?: string | null;
    retainIndexChangedToLowOn?: string | null;
  } | null;
}

export const usePredictiveIndexStudents = ({
  orgId,
  orgIdLoading,
  orgIdError,
  indexType,
  currentIndex,
  previousIndex,
  funnelStates,
  inCommunity,
  sortField,
  sortDirection = SortDirectionEnum.Desc,
  first = 20,
  last,
  after,
  before,
}: UsePredictiveIndexStudentsParams): UsePredictiveIndexStudentsReturn => {
  const defaultSortField =
    indexType === "commit"
      ? StudentListCommitIndexSortableFieldEnum.CommitScore
      : StudentListRisePredictiveIndexSortableFieldEnum.RisePredictiveIndexScore;

  const effectiveSortField = sortField ?? defaultSortField;
  const variables = useMemo<PredictiveIndexStudentsQueryVariables>(() => {
    const argument: StudentListArgumentInput = buildArgumentForIndexType(
      indexType,
      currentIndex,
      previousIndex,
      funnelStates,
      inCommunity,
      effectiveSortField,
      sortDirection,
    );

    return {
      orgId: orgId || "",
      argument,
      first,
      last,
      afterCursor: after,
      beforeCursor: before,
    };
  }, [
    orgId,
    indexType,
    currentIndex,
    previousIndex,
    funnelStates,
    inCommunity,
    effectiveSortField,
    sortDirection,
    first,
    last,
    after,
    before,
  ]);

  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery(PREDICTIVE_INDEX_STUDENTS, {
    variables,
    skip: !orgId || orgIdLoading || !!orgIdError,
    notifyOnNetworkStatusChange: true,
  });

  const loading = orgIdLoading || queryLoading;
  const error = orgIdError || queryError;

  const students = useMemo<PredictiveIndexStudent[]>(() => {
    if (
      !data?.node ||
      data.node.__typename !== "Organization" ||
      !data.node.studentList?.edges
    ) {
      return [];
    }

    return data.node.studentList.edges
      .map((edge) => {
        if (!edge || !edge.node) {
          return null;
        }

        const student = edge.node;
        const orgStatus = student.studentOrganizationStatus;

        return transformStudentForIndexType(
          student,
          orgStatus,
          indexType,
          currentIndex,
        );
      })
      .filter((student): student is PredictiveIndexStudent => student !== null);
  }, [data, indexType, currentIndex]);

  const totalCount = useMemo(() => {
    if (!data?.node || data.node.__typename !== "Organization") {
      return 0;
    }
    return data.node.studentList.totalCount;
  }, [data]);

  const pageInfo = useMemo(() => {
    if (!data?.node || data.node.__typename !== "Organization") {
      return {
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: null,
        endCursor: null,
      };
    }

    const rawPageInfo = data.node.studentList.pageInfo;
    return {
      hasNextPage: rawPageInfo.hasNextPage,
      hasPreviousPage: rawPageInfo.hasPreviousPage,
      startCursor: rawPageInfo.startCursor ?? null,
      endCursor: rawPageInfo.endCursor ?? null,
    };
  }, [data]);

  return {
    students,
    loading,
    error,
    totalCount,
    pageInfo,
    refetch,
  };
};

function buildArgumentForIndexType(
  indexType: IndexType,
  currentIndex?: PredictionIndexEnum | RisePredictiveIndexEnum,
  previousIndex?: PredictionIndexEnum | RisePredictiveIndexEnum,
  funnelStates?: FunnelStateEnum[],
  inCommunity?: boolean,
  sortField?:
    | StudentListCommitIndexSortableFieldEnum
    | StudentListRisePredictiveIndexSortableFieldEnum
    | StudentListOrganizationSortableFieldEnum,
  sortDirection?: SortDirectionEnum,
): StudentListArgumentInput {
  if (indexType === "commit") {
    const filters = {
      commitIndex: currentIndex
        ? [currentIndex as PredictionIndexEnum]
        : undefined,
      previousCommitIndex: previousIndex
        ? [previousIndex as PredictionIndexEnum]
        : undefined,
      funnelStates,
      inCommunity:
        inCommunity !== undefined
          ? inCommunity
            ? YesNoEnum.Yes
            : YesNoEnum.No
          : undefined,
    };

    const isCommitIndexField =
      sortField &&
      Object.values(StudentListCommitIndexSortableFieldEnum).includes(
        sortField as StudentListCommitIndexSortableFieldEnum,
      );

    const ordering = isCommitIndexField
      ? {
          commitIndexOrdering: {
            field: sortField as StudentListCommitIndexSortableFieldEnum,
            direction: sortDirection || SortDirectionEnum.Desc,
          },
        }
      : {
          organizationOrdering: {
            field: sortField as StudentListOrganizationSortableFieldEnum,
            direction: sortDirection || SortDirectionEnum.Desc,
          },
        };

    return {
      commitIndex: {
        filters,
        ordering: [ordering],
      },
    };
  } else {
    const riseFilters = {
      currentIndex: currentIndex as RisePredictiveIndexEnum,
      previousIndex: previousIndex as RisePredictiveIndexEnum,
      funnelStates,
      inCommunity:
        inCommunity !== undefined
          ? inCommunity
            ? YesNoEnum.Yes
            : YesNoEnum.No
          : undefined,
    };

    const ordering = {
      field: sortField as StudentListRisePredictiveIndexSortableFieldEnum,
      direction: sortDirection || SortDirectionEnum.Desc,
    };

    const riseIndexMap = {
      riseApply: { applyIndex: { filters: riseFilters, ordering: [ordering] } },
      riseCommit: {
        commitIndex: { filters: riseFilters, ordering: [ordering] },
      },
      riseMelt: { meltIndex: { filters: riseFilters, ordering: [ordering] } },
      riseYield: { yieldIndex: { filters: riseFilters, ordering: [ordering] } },
      riseRetain: {
        retainIndex: { filters: riseFilters, ordering: [ordering] },
      },
    };

    return {
      rise: riseIndexMap[indexType] as StudentListArgumentRiseInput,
    };
  }
}

function transformStudentForIndexType(
  student: StudentNode,
  orgStatus: StudentOrganizationStatus | null | undefined,
  indexType: IndexType,
  currentIndex?: PredictionIndexEnum | RisePredictiveIndexEnum,
): PredictiveIndexStudent | null {
  const getScoreForIndexType = (): number => {
    switch (indexType) {
      case "commit":
        return orgStatus?.commitScore || 0;
      case "riseCommit":
        return orgStatus?.rise?.commitScore || 0;
      case "riseApply":
        return orgStatus?.rise?.applyScore || 0;
      case "riseMelt":
        return orgStatus?.rise?.meltScore || 0;
      case "riseYield":
        return orgStatus?.rise?.yieldScore || 0;
      case "riseRetain":
        return orgStatus?.rise?.retainScore || 0;
      default:
        return 0;
    }
  };

  const getPreviousIndexForIndexType = ():
    | PredictionIndexEnum
    | RisePredictiveIndexEnum
    | null => {
    switch (indexType) {
      case "commit":
        return orgStatus?.previousCommitIndex || null;
      case "riseCommit":
        return orgStatus?.rise?.previousCommitIndex || null;
      case "riseApply":
        return orgStatus?.rise?.previousApplyIndex || null;
      case "riseMelt":
        return orgStatus?.rise?.previousMeltIndex || null;
      case "riseYield":
        return orgStatus?.rise?.previousYieldIndex || null;
      case "riseRetain":
        return orgStatus?.rise?.previousRetainIndex || null;
      default:
        return null;
    }
  };

  const getChangeDateForIndexType = (): string | null => {
    if (!currentIndex) return null;

    const getDateFieldName = (index: string, indexValue: string): string => {
      return `${index}IndexChangedTo${indexValue}On`;
    };

    let indexName: string;
    let indexValueName: string;

    switch (indexType) {
      case "commit":
        indexName = "commit";
        break;
      case "riseCommit":
        indexName = "commit";
        break;
      case "riseApply":
        indexName = "apply";
        break;
      case "riseMelt":
        indexName = "melt";
        break;
      case "riseYield":
        indexName = "yield";
        break;
      case "riseRetain":
        indexName = "retain";
        break;
      default:
        return null;
    }

    if (indexType === "commit") {
      switch (currentIndex as PredictionIndexEnum) {
        case PredictionIndexEnum.High:
          indexValueName = "High";
          break;
        case PredictionIndexEnum.Medium:
          indexValueName = "Medium";
          break;
        case PredictionIndexEnum.Low:
          indexValueName = "Low";
          break;
        default:
          return null;
      }
      const dateField = getDateFieldName(indexName, indexValueName);
      return (
        (orgStatus as Record<string, string | null | undefined>)?.[dateField] ||
        null
      );
    } else {
      switch (currentIndex as RisePredictiveIndexEnum) {
        case RisePredictiveIndexEnum.High:
          indexValueName = "High";
          break;
        case RisePredictiveIndexEnum.MediumHigh:
          indexValueName = "MediumHigh";
          break;
        case RisePredictiveIndexEnum.Medium:
          indexValueName = "Medium";
          break;
        case RisePredictiveIndexEnum.MediumLow:
          indexValueName = "MediumLow";
          break;
        case RisePredictiveIndexEnum.Low:
          indexValueName = "Low";
          break;
        default:
          return null;
      }
      const dateField = getDateFieldName(indexName, indexValueName);
      return (
        (orgStatus?.rise as Record<string, string | null | undefined>)?.[
          dateField
        ] || null
      );
    }
  };

  const getInCommunity = (): boolean => {
    return orgStatus?.inCommunity === YesNoEnum.Yes;
  };

  return {
    id: student.id,
    name: orgStatus?.name || "",
    score: getScoreForIndexType(),
    previousIndex: getPreviousIndexForIndexType(),
    funnelState: orgStatus?.funnelState || FunnelStateEnum.Inquired,
    indexChangedOn: getChangeDateForIndexType(),
    imageUrl: student.profilePhoto?.url || null,
    inCommunity: getInCommunity(),
  };
}
